import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Toaster } from "@/components/ui/toaster";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ResearchPage from "@/pages/ResearchPage";
import ContactPage from "@/pages/ContactPage";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { GetInvolvedModalProvider, useGetInvolvedModal } from "@/contexts/GetInvolvedModalContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import GetInvolvedModal from "@/components/GetInvolvedModal";
import AdminProtectedRoute from "@/components/AdminProtectedRoute";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminAdmins from "@/pages/admin/AdminAdmins";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminBanner from "@/pages/admin/AdminBanner";
import AdminReports from "@/pages/admin/AdminReports";
import AdminLeads from "@/pages/admin/AdminLeads";
import NewsPage from "@/pages/NewsPage";
import ReportsPage from "@/pages/ReportsPage";
import AdminNews from "@/pages/admin/AdminNews";
import AdminMasterData from "@/pages/admin/AdminMasterData";
import AdminPopups from "@/pages/admin/AdminPopups";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminPetitions from "@/pages/admin/AdminPetitions";
import AdminContent from "@/pages/admin/AdminContent";
import AdminTimelinePictures from "@/pages/admin/AdminTimelinePictures";
import GalleryPage from "@/pages/GalleryPage";
import AlbumDetailPage from "@/pages/AlbumDetailPage";
import PetitionPage from "@/pages/PetitionPage";

// Renders the modal at root level — outside any backdrop-filter stacking context
function GlobalModals() {
  const { isOpen, closeModal } = useGetInvolvedModal();
  return <GetInvolvedModal isOpen={isOpen} onClose={closeModal} />;
}

function App() {
  return (
    <AdminAuthProvider>
      <LanguageProvider>
        <GetInvolvedModalProvider>
        <BrowserRouter>
          <Routes>
            {/* Public site */}
            <Route
              path="/*"
              element={
                <div className="App min-h-screen flex flex-col" style={{ backgroundColor: "#F7F5F0" }}>
                  <Navbar />
                  <main className="flex-1">
                    <Routes>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/about" element={<AboutPage />} />
                      <Route path="/research" element={<ResearchPage />} />
                      <Route path="/news" element={<NewsPage />} />
                      <Route path="/gallery" element={<GalleryPage />} />
                      <Route path="/gallery/:albumId" element={<AlbumDetailPage />} />
                      <Route path="/reports" element={<ReportsPage />} />
                      <Route path="/petition" element={<PetitionPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                    </Routes>
                  </main>
                  <Footer />
                </div>
              }
            />

            {/* Admin routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route
              path="/admin/dashboard"
              element={
                <AdminProtectedRoute>
                  <AdminLayout><AdminDashboard /></AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/admins"
              element={
                <AdminProtectedRoute requireSuperAdmin>
                  <AdminLayout><AdminAdmins /></AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/gallery"
              element={
                <AdminProtectedRoute>
                  <AdminLayout><AdminGallery /></AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/banner"
              element={
                <AdminProtectedRoute>
                  <AdminLayout><AdminBanner /></AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/timeline-pictures"
              element={
                <AdminProtectedRoute>
                  <AdminLayout><AdminTimelinePictures /></AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/kybia-reports"
              element={
                <AdminProtectedRoute>
                  <AdminLayout>
                    <AdminReports
                      title="KYBIA Reports"
                      endpoint="kybia-reports"
                      includeCategory={false}
                      itemLabel="KYBIA Report"
                      addButtonLabel="Add KYBIA Report"
                      emptyLabel="No KYBIA reports yet"
                    />
                  </AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/reports"
              element={
                <AdminProtectedRoute>
                  <AdminLayout><AdminReports /></AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/news"
              element={
                <AdminProtectedRoute>
                  <AdminLayout><AdminNews /></AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/master-data/report-categories"
              element={
                <AdminProtectedRoute>
                  <AdminLayout><AdminMasterData /></AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/content"
              element={
                <AdminProtectedRoute>
                  <AdminLayout><AdminContent /></AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/popups"
              element={
                <AdminProtectedRoute>
                  <AdminLayout><AdminPopups /></AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/leads"
              element={
                <AdminProtectedRoute requireShowLeads>
                  <AdminLayout><AdminLeads /></AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <AdminProtectedRoute>
                  <AdminLayout><AdminSettings /></AdminLayout>
                </AdminProtectedRoute>
              }
            />
            <Route
              path="/admin/petitions"
              element={
                <AdminProtectedRoute>
                  <AdminLayout><AdminPetitions /></AdminLayout>
                </AdminProtectedRoute>
              }
            />
          </Routes>
          <Toaster />
          <GlobalModals />
        </BrowserRouter>
      </GetInvolvedModalProvider>
    </LanguageProvider>
    </AdminAuthProvider>
  );
}

export default App;
